// What a CREATION SESSION is allowed to offer (busy-my-web#253).
//
// Fatemeh: "ما وقتی یه چیزی میخایم بسازیم، به طور مثال … ساخت ایمیل، دیگه وب سرچ نداریم … حواست
// باشه این در همه module creation ها یکیه" — while something is being MADE in the universal input,
// the web search is not on offer, and that is one rule across every module, not a rule about email.
//
// Writing something is a different act from asking the internet a question: the box is a form for
// as long as a module is up, so the globe (and the follow-ups/suggestions that would start a
// search) have nothing to do there. The one place they could still be reached from — the globe
// button — is also the one gesture that THROWS THE FORM AWAY, which is the worst thing a stray
// click can do next to a half-written email.
//
// So the capability is asked HERE, once, by everything that can offer a search: the action row's
// globe, the box's own suggestion registry and the Follow-ups strip beside it. A per-module copy
// of the same `v-if` is exactly how "email has no web search but a contact still does" happens.
//
// Plain functions over a plain session shape, so the rule is testable without mounting the box.

/**
 * The session, as the box knows it:
 *   `quickMode` — 'tasks' | 'notes' | 'events' | 'emails' | 'contacts' | null (the in-box modes)
 *   `formMode`  — the full creation FORM is open (the research pack's own creation lives here too)
 *
 * NO SESSION IS A SESSION TOO — null, undefined, or nothing at all, all meaning "the box is not
 * making anything". A parameter default only fires for `undefined`, so the `= {}` this used to
 * carry threw a TypeError on an explicit `null` — which is exactly what `useSuggestions`' own
 * documented no-argument signature hands over (its default is null). The gate is the one place
 * that knows what a session IS, so it is the place that says a missing one is not one.
 */
export function isCreationSession(session) {
  const { quickMode = null, formMode = false } = session || {}
  return !!quickMode || !!formMode
}

/**
 * Is the page FOCUSED on one thing right now? (busy-my-web#255)
 *
 * Fatemeh: "توی چندین حالت صفحه خالی میشه و کاربر فقط روی همون کاری که داره انجام میده تمرکز میکنه"
 * — in three situations the page empties so the user concentrates on the act at hand: ticking items
 * in a web search, CREATING anything in the universal input, and EDITING anything in it. While one
 * of them is up the Follow-ups strip and the Focus block stand down, the centre messages ("Good
 * morning") go with them, and the Ongoing list and the Timeline rail trim to the one thing being
 * worked on.
 *
 * It is a WIDER question than `isCreationSession` and deliberately built on top of it rather than
 * beside it: a creation session is always focused, but so is an edit (which is a creation session
 * with an entity behind it — the box sets `quickMode` for both) and so is a selection in a search,
 * which makes nothing at all. Two sibling flags would have been two answers to "is the page
 * emptied?", and the surfaces that ask are on four different rails.
 *
 * The session, as the box publishes it (store module `focusedSession`):
 *   `quickMode` / `formMode` — as above, the creation half
 *   `editing`   — the entity open in the box: { menu, id?, ongoingId?, title? }, or null
 *   `selecting` — items are ticked in a web search
 *   `replying`  — the box is a reply / forward to an open thread (busy-my-web#480): exempt
 */
export function isFocusedSession(session) {
  const { editing = null, selecting = false, replying = false } = session || {}
  // A REPLY is not a focused session (busy-my-web#480, 2026-09-15 parity audit D-7; frame
  // 40000017:13522): the conversation being answered stays on the page with its Follow-ups, its
  // Focus and its Email timeline — the box turns into the reply card, nothing else steps aside.
  // The #255 rule is unchanged for every creation and edit; a reply is the one exemption.
  if (replying) return false
  return isCreationSession(session) || !!editing || !!selecting
}

/**
 * Is the web search on offer at all right now? The single gate every surface consults.
 *
 * …with the REPLY CARD as #253's one exemption (busy-my-web#480, 2026-09-15 parity audit; frame
 * 40000017:13522 draws the card's whole strip as "+ globe … arrow mic"). #253's reasoning was that
 * the globe is the gesture that throws a half-written form away — but the reply card is not a form
 * the page emptied itself for: the thread it answers is still on screen behind it, and the frame
 * puts the globe there, so answering an email can go and look something up.
 */
export function webSearchOffered(session) {
  if (session?.replying) return true
  return !isCreationSession(session)
}

/**
 * The suggestion/follow-up actions that ARE a web search — the chips that would arm the globe or
 * carry a research on. Named by action id, because that is what the registry and the bridge
 * (`window.__busySuggest`) both speak.
 */
export const WEB_SEARCH_ACTIONS = ['searchWeb', 'newResearch', 'continueResearch']

export function isWebSearchAction(action) {
  return WEB_SEARCH_ACTIONS.includes(String(action || ''))
}

/**
 * A suggestion list with the web-search entries removed while a creation session is up. The list is
 * returned UNTOUCHED (same array) outside creation mode, so nothing changes for the search flows
 * this rule is not about.
 */
export function offeredSuggestions(list, session) {
  const all = list || []
  if (webSearchOffered(session)) return all
  return all.filter(s => !isWebSearchAction(s?.action))
}

export default webSearchOffered
